import { VersionDtoV2, VersionPropertyDto } from '@domain/api/generated/sapi';
import { IVersion, IVersionProperty } from '@domain/creativeset/version';
import { isVersionedFeed } from '../../elements/feed/feeds.utils';
import { isVersionedText, isVersionedWidgetText } from '../../elements/rich-text/utils';
import { serializeVersionedFeed } from './versionedFeed-serializer';
import { serializeVersionedText } from './versionedText-serializer';
import { fixFeedVersionProperties, fixTextVersionProperty } from './versions-semantic-validation';
import { serializeWidgetText } from './widgetText-serializer';

export function serializeVersions(
    versions: IVersion[],
    sentinelService?: { trackPageAction: (message: string, data?: Record<string, unknown>) => void }
): VersionDtoV2[] {
    const serializedVersions: VersionDtoV2[] = [];
    for (const version of versions) {
        const serializedVersion = serializeVersion(version, sentinelService);
        serializedVersions.push(serializedVersion);
    }

    return serializedVersions;
}

export function serializeVersion(
    version: IVersion,
    sentinelService?: { trackPageAction: (message: string, data?: Record<string, unknown>) => void }
): VersionDtoV2 {
    const serializedVersionProperties = serializeVersionProperties(
        version.properties,
        true,
        sentinelService
    );
    return {
        id: version.id,
        name: version.name,
        targetUrl: version.targetUrl,
        localization: {
            id: version.localization.id
        },
        properties: serializedVersionProperties
    };
}

export function serializeVersionProperties(
    versionProperties: IVersionProperty[],
    shouldFixVersions: boolean,
    sentinelService?: { trackPageAction: (message: string, data?: Record<string, unknown>) => void }
): VersionPropertyDto[] {
    const serializedVersionProperties: VersionPropertyDto[] = [];
    for (const versionProperty of versionProperties) {
        const serializedVersionProperty = serializeVersionProperty(
            versionProperty,
            shouldFixVersions,
            sentinelService
        );
        serializedVersionProperties.push(serializedVersionProperty);
    }
    return serializedVersionProperties;
}

export function serializeVersionProperty(
    versionProperty: IVersionProperty,
    shouldFixVersions: boolean = true,
    sentinelService?: { trackPageAction: (message: string, data?: Record<string, unknown>) => void }
): VersionPropertyDto {
    if (isVersionedFeed(versionProperty)) {
        if (shouldFixVersions) {
            fixFeedVersionProperties(versionProperty, sentinelService);
        }

        return {
            name: 'feed',
            id: versionProperty.id,
            value: serializeVersionedFeed(versionProperty)
        };
    }
    if (isVersionedWidgetText(versionProperty)) {
        return {
            name: 'widgetText',
            id: versionProperty.id,
            value: serializeWidgetText(versionProperty)
        };
    }
    if (isVersionedText(versionProperty)) {
        if (shouldFixVersions) {
            fixTextVersionProperty(versionProperty, sentinelService);
        }

        return {
            name: 'content',
            id: versionProperty.id,
            value: serializeVersionedText(versionProperty)
        };
    }
    throw new Error('Invalid versionProperty type');
}
